import * as React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { usePendingInvitations, useAcceptShare, useRejectShare } from '@/hooks/useShares';
import { ShareService } from '@/services/ShareService';
import { formatDate } from '@/lib/utils';
import { toast } from 'sonner';

const DISMISSED_KEY = 'dismissedInvitationIds';

function readDismissed(): Set<string> {
  try {
    const raw = sessionStorage.getItem(DISMISSED_KEY);
    return new Set(raw ? (JSON.parse(raw) as string[]) : []);
  } catch {
    return new Set();
  }
}

function persistDismissed(ids: Set<string>) {
  try {
    sessionStorage.setItem(DISMISSED_KEY, JSON.stringify([...ids]));
  } catch {
    // storage unavailable; the dialog will just reappear next load
  }
}

/**
 * Global prompt shown when someone shares a budget with the current user.
 * Accepting refreshes the budget lists so the new shared budget appears
 * immediately on the dashboard; declining removes the invitation.
 * Dismissing hides the prompt for the session (still reachable via the
 * invitations page).
 *
 * Takes priority over the unassigned-spending prompt so collaborators can
 * accept a shared budget before placing captures into it.
 */
export function InvitationPromptDialog({
  onOpenChange,
}: {
  onOpenChange?: (open: boolean) => void;
} = {}) {
  const { pendingInvitations } = usePendingInvitations();
  const acceptShare = useAcceptShare();
  const rejectShare = useRejectShare();
  const [dismissedIds, setDismissedIds] = React.useState<Set<string>>(readDismissed);
  const [busyId, setBusyId] = React.useState<string | null>(null);

  const visibleInvitations = pendingInvitations.filter(
    (invitation) => !dismissedIds.has(invitation.shareId)
  );
  const open = visibleInvitations.length > 0;

  React.useEffect(() => {
    onOpenChange?.(open);
    return () => onOpenChange?.(false);
  }, [open, onOpenChange]);

  const dismissAll = () => {
    const next = new Set(dismissedIds);
    visibleInvitations.forEach((invitation) => next.add(invitation.shareId));
    setDismissedIds(next);
    persistDismissed(next);
  };

  const handleAccept = async (shareId: string, budgetName: string) => {
    setBusyId(shareId);
    try {
      await acceptShare.mutateAsync(shareId);
      toast.success(`"${budgetName}" was added to your budgets`);
    } catch {
      toast.error('Failed to accept the invitation. Please try again.');
    } finally {
      setBusyId(null);
    }
  };

  const handleReject = async (shareId: string) => {
    setBusyId(shareId);
    try {
      await rejectShare.mutateAsync(shareId);
      toast.success('Invitation declined');
    } catch {
      toast.error('Failed to decline the invitation. Please try again.');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && dismissAll()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {visibleInvitations.length === 1
              ? 'Budget shared with you'
              : `${visibleInvitations.length} budgets shared with you`}
          </DialogTitle>
          <DialogDescription>
            Accept to add the budget to your list, or decline the invitation.
          </DialogDescription>
        </DialogHeader>

        <ul className="space-y-3">
          {visibleInvitations.map((invitation) => (
            <li
              key={invitation.shareId}
              className="flex flex-wrap items-center justify-between gap-3 rounded-md border p-3"
            >
              <div className="min-w-0">
                <p className="font-medium text-foreground truncate">{invitation.budgetName}</p>
                <p className="text-xs text-muted-foreground">
                  From {invitation.ownerEmail} · {ShareService.getRoleName(invitation.role)} ·{' '}
                  {formatDate(invitation.invitedAt)}
                </p>
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={() => handleAccept(invitation.shareId, invitation.budgetName)}
                  loading={busyId === invitation.shareId && acceptShare.isPending}
                  disabled={busyId !== null && busyId !== invitation.shareId}
                >
                  Accept
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleReject(invitation.shareId)}
                  loading={busyId === invitation.shareId && rejectShare.isPending}
                  disabled={busyId !== null && busyId !== invitation.shareId}
                >
                  Decline
                </Button>
              </div>
            </li>
          ))}
        </ul>

        <DialogFooter>
          <Button variant="ghost" onClick={dismissAll}>
            Later
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
